import React, { useCallback } from "react";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import { useIntl } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";
import NotificationSettingMileageTracking from "@app/types/notifications/NotificationSettingMileageTracking";

import styles from "../../NotificationsCommon.module.scss";
import Card from "../Card";
import Header from "../Header";

import getMileageTrackingUpdate from "./utils/getMileageTrackingUpdate";

interface MileageTrackingProps {
  form: FormInstance<NotificationSettingValue>;
  onToggle: () => void;
}

const MileageTracking: React.FC<MileageTrackingProps> = ({ form, onToggle }) => {
  const intl = useIntl();
  const handleToggleMileageTracking = useCallback(
    (enabled: boolean) => {
      form.setFieldsValue({ mileageTracking: getMileageTrackingUpdate(enabled) });
      onToggle();
    },
    [form, onToggle]
  );

  const handleChangeSetting = useCallback(
    (name: keyof Omit<NotificationSettingMileageTracking, "enabled">) => (enabled: boolean) => {
      form.setFieldsValue({ mileageTracking: { [name]: enabled } });
    },
    [form]
  );

  return (
    <Card>
      <Form.Item hidden name="mileageTracking">
        <div />
      </Form.Item>
      <Form.Item
        shouldUpdate={(prev, next) => prev.mileageTracking.enabled !== next.mileageTracking.enabled}
      >
        {({ getFieldValue }) => (
          <Header
            checked={getFieldValue(["mileageTracking", "enabled"])}
            description={intl.formatMessage({
              id: "settings.notifications.mileageTracking.text",
              defaultMessage: "Get notified auto-track settings and classification reminders.",
              description: "Text of settings notifications MileageTracking card",
            })}
            onChange={handleToggleMileageTracking}
            title={intl.formatMessage({
              id: "settings.notifications.mileageTracking.title",
              defaultMessage: "Mileage Tracking",
              description: "Title of settings notifications MileageTracking card",
            })}
          />
        )}
      </Form.Item>

      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.mileageTracking.enabled !== next.mileageTracking.enabled ||
          prev.mileageTracking.weeklyTrackedTrips !== next.mileageTracking.weeklyTrackedTrips
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["mileageTracking", "weeklyTrackedTrips"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["mileageTracking", "enabled"])}
            onChange={handleChangeSetting("weeklyTrackedTrips")}
          >
            {intl.formatMessage({
              id: "settings.notifications.mileageTracking.weeklyTrackedTrips",
              defaultMessage: "Weekly summary of drives",
              description: "Settings notifications MT - Weekly summary of drives",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.mileageTracking.enabled !== next.mileageTracking.enabled ||
          prev.mileageTracking.weeklyClassificationReminder !==
            next.mileageTracking.weeklyClassificationReminder
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["mileageTracking", "weeklyClassificationReminder"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["mileageTracking", "enabled"])}
            onChange={handleChangeSetting("weeklyClassificationReminder")}
          >
            {intl.formatMessage({
              id: "settings.notifications.mileageTracking.weeklyClassificationReminder",
              defaultMessage: "Weekly classification reminders",
              description: "Settings notifications MT - Weekly classification reminders",
            })}
          </Checkbox>
        )}
      </Form.Item>
    </Card>
  );
};

export default MileageTracking;
